import {
  Audience,
  AutomationActionStatus,
  AutomationStatus,
  IAutomationDenormalized,
  IExperienceTemplate,
  ITaskDenormalized
} from '@dadohr/shared/domainTypes/experience';
import { useTheme } from '@emotion/react';
import { Button, Tooltip, Typography } from '@material-ui/core';
import { PlayCircle, Redo, RefreshCw, Undo } from 'lucide-react';
import moment from 'moment-timezone';
import { ButtonWithoutPropagation } from '../../../../../components/ButtonWithoutPropagation';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { ColoredChipLight } from '../../../../../components/ColoredChip';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../services/enumerate';
import { getItemDisplayName } from '../../../helpers/adminName';
import { AutomationStatusChip } from '../../../components/AutomationStatusChip';
import { ActionErrorDetails, ActionWithIcon } from '../ParticipantDialog';
import { ActorLegend } from './ActorLegend';
import { AudienceHint } from './AudienceHint';
import {
  ErrorText,
  InnerItemDetail,
  InnerItemGroup,
  InnerItemList,
  InnerItemRow,
  ItemTitle
} from './CommonStyledComponents';
import { CustomizedTimingHint } from './CustomizedTimingHint';
import { DateWithTooltip } from './DateWithTooltip';
import { ForceTriggerButton } from './ForceTriggerButton';
import { ModifiedChip } from './ModifiedChip';
import { SelectableListItem } from './SelectableListItem';

const ALLOW_REOPEN_AUTOMATION = false;

const ActionStatusChip = <
  T extends {
    // Display status: the runtime AutomationActionStatus plus the synthetic
    // 'RUNNING' the denormalizer adds for an in-flight sync action.
    status: AutomationActionStatus | 'RUNNING';
    errorData?: any;
  }
>({
  action
}: {
  action: T;
}) => {
  const { t } = useUnsafeTranslation();
  switch (action.status) {
    case AutomationActionStatus.DONE:
      return (
        <ColoredChipLight color="green" size="small">
          {t('Completed')}
        </ColoredChipLight>
      );
    case AutomationActionStatus.SKIPPED:
      return (
        <ColoredChipLight color="darkGray" size="small">
          {t('Skipped')}
        </ColoredChipLight>
      );
    case 'RUNNING':
      return (
        <ColoredChipLight color="blue" size="small">
          {t('Running')}
        </ColoredChipLight>
      );
    case AutomationActionStatus.UPCOMING:
      return (
        <ColoredChipLight color="gray" size="small">
          {t('Pending')}
        </ColoredChipLight>
      );
    case AutomationActionStatus.ERROR:
      return (
        <ColoredChipLight color="red" size="small">
          <Tooltip title={action.errorData?.type || ''}>
            <span>{t('Error')}</span>
          </Tooltip>
        </ColoredChipLight>
      );
    default:
      return null;
  }
};

export const Automation = ({
  a,
  onSkipAutomation,
  onUnskipAutomation,
  onRunToday,
  onSkipAction,
  onUnskipAction,
  onReopenAutomation,
  onForceRunAutomationAction,
  onForceRunAutomationActions,
  onForceTrigger,
  selected,
  onSelect,
  audience,
  customizedTiming,
  checkIfActionModified,
  isModified,
  supportingActors,
  isSelectable,
  triggeredBy,
  template
}: {
  a: IAutomationDenormalized;
  onSkipAutomation: () => void;
  onUnskipAutomation: () => void;
  checkIfActionModified: (id: string, actionId: string) => boolean;
  onRunToday: () => void;
  onSkipAction: (actionTplId: string) => void;
  onUnskipAction: (actionTplId: string) => void;
  onReopenAutomation: () => void;
  onForceRunAutomationAction: (actionTplId: string) => Promise<any>;
  onForceRunAutomationActions: (actionTplIds: string[]) => Promise<any>;
  onForceTrigger?: () => Promise<any>;
  selected?: boolean;
  onSelect?: (nextSelected: boolean) => void;
  audience?: Audience | null;
  customizedTiming?: boolean;
  isModified?: boolean;
  supportingActors: IExperienceTemplate['supportingActors'];
  isSelectable?: boolean;
  triggeredBy?: ITaskDenormalized[];
  template?: IExperienceTemplate
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();

  const { timing } = a;
  if (timing.type !== 'ABSOLUTE') {
    console.warn('TIMING_NOT_SUPPORTED_YET', a);
    return null;
  }

  const isInFuture = (): boolean => {
    if (timing.start) {
      const automationDate = moment.unix(timing.start.seconds);
      const days = automationDate.diff(new Date(), 'days');
      if (days > 0) {
        return true;
      }
    }
    return false;
  };

  const isUntriggered = !!triggeredBy?.length;

  return (
    <SelectableListItem
      isSelectable={
        /* looks weird but is needed because of default */
        isUntriggered ? false : isSelectable
      }
      selected={selected}
      onSelect={onSelect}
      header={
        <>
          <AutomationStatusChip status={a.status} autoWidth />
          <ActorLegend
            type="automation"
            color={theme.custom.colors.purple.w500}
          >
            {t('Automation')}
          </ActorLegend>
          <ItemTitle>
            <strong>{a.title}</strong>
            <AudienceHint type="automation" audience={audience} />
            <CustomizedTimingHint
              type="automation"
              customizedTiming={customizedTiming}
              at={timing.start}
            />
            <ModifiedChip active={isModified} />
          </ItemTitle>
          {ALLOW_REOPEN_AUTOMATION &&
            (a.status === AutomationStatus.DONE ||
              a.status === AutomationStatus.ERROR) && (
              <Button
                onClick={onReopenAutomation}
                variant="outlined"
                color="primary"
                size="small"
              >
                {t('Re-run automation')}
              </Button>
            )}
        </>
      }
      headerActions={
        <>
          {!isUntriggered && (
            <>
              {(a.status === AutomationStatus.UPCOMING ||
                a.status === AutomationStatus.ERROR) && (
                <ButtonWithoutPropagation
                  startIcon={<Redo size={16} />}
                  variant="outlined"
                  type="button"
                  size="small"
                  onClick={() => onSkipAutomation()}
                >
                  {t('Skip')}
                </ButtonWithoutPropagation>
              )}
              {isInFuture() && a.status === AutomationStatus.UPCOMING && (
                <ButtonWithoutPropagation
                  variant="outlined"
                  startIcon={<PlayCircle size={16} />}
                  type="button"
                  size="small"
                  onClick={() => onRunToday()}
                >
                  {t('Run today')}
                </ButtonWithoutPropagation>
              )}
              {a.status === AutomationStatus.ERROR && (
                <ButtonWithPromise
                  startIcon={<RefreshCw size={16} />}
                  variant="outlined"
                  size="small"
                  pending={`${t('Retrying')}...`}
                  onClick={() =>
                    onForceRunAutomationActions(
                      a.actions
                        .filter(
                          (ac) => ac.status === AutomationActionStatus.ERROR
                        )
                        .map((ac) => ac.templateId)
                    )
                  }
                  noPropagation
                >
                  {t('Retry errors')}
                </ButtonWithPromise>
              )}
              {a.status === AutomationStatus.SKIPPED && !a.completedAt && (
                <ButtonWithoutPropagation
                  startIcon={<Undo size={16} />}
                  variant="outlined"
                  size="small"
                  onClick={(e) => onUnskipAutomation()}
                >
                  {t('Unskip')}
                </ButtonWithoutPropagation>
              )}
            </>
          )}
          
          {template &&
            <ForceTriggerButton
              item={a}
              itemType="automation"
              triggeredBy={triggeredBy || []}
              isUntriggered={isUntriggered}
              template={template}
              onForceTrigger={onForceTrigger}
              enqueueSnackbarSuccess={enqueueSnackbarSuccess}
              enqueueSnackbarError={enqueueSnackbarError}
              t={t}
            />
          }
        </>

      }
      subHeader={
        triggeredBy?.length ? (
          <Typography variant="caption" component="div">
            {t('After completion of')}:{' '}
            <strong>
              {joinWithCommaAndAnd(
                triggeredBy.map((x) => getItemDisplayName(x)),
                t
              )}
            </strong>
          </Typography>
        ) : (
          <Typography variant="caption" component="div">
            {a.completedAt && a.status !== AutomationStatus.UPCOMING && (
              <>
                {a.completedAt ? (
                  <DateWithTooltip
                    textBefore={`${t('Ran on')}`}
                    date={a.completedAt}
                  />
                ) : (
                  '???'
                )}
              </>
            )}
            {!a.completedAt &&
              (a.status === AutomationStatus.UPCOMING ||
                a.status === AutomationStatus.SKIPPED) && (
                <DateWithTooltip
                  textBefore={
                    a.status === AutomationStatus.SKIPPED
                      ? `${t('Would have run on')}`
                      : `${t('Will run on')}`
                  }
                  date={timing.start}
                />
              )}
          </Typography>
        )
      }
    >
      <InnerItemList>
        <InnerItemGroup>
          {a.actions.map((ac) => (
            <InnerItemRow key={ac.id}>
              <FlexContainer justifyContent={'space-between'} fullWidth={true}>
                <InnerItemDetail>
                  <ActionStatusChip action={ac} />
                  <ActionWithIcon action={ac} />
                  {ac.status === AutomationActionStatus.ERROR && (
                    <ErrorText>
                      <ActionErrorDetails
                        action={ac}
                        supportingActors={supportingActors}
                      />
                    </ErrorText>
                  )}
                  <ModifiedChip
                    active={checkIfActionModified(a.templateId, ac.templateId)}
                  />
                </InnerItemDetail>
                {!isUntriggered && (
                  <FlexContainer>
                    {(ac.status === AutomationActionStatus.UPCOMING ||
                      ac.status === AutomationActionStatus.ERROR) && (
                      <Button
                        startIcon={<Redo size={16} />}
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          onSkipAction(ac.templateId);
                          e.preventDefault();
                        }}
                      >
                        {t('Skip')}
                      </Button>
                    )}

                    {ac.status === AutomationActionStatus.ERROR && (
                      <ButtonWithPromise
                        startIcon={<RefreshCw size={16} />}
                        variant="outlined"
                        size="small"
                        pending={`${t('Retrying')}...`}
                        onClick={() =>
                          onForceRunAutomationAction(ac.templateId)
                        }
                      >
                        {t('Retry')}
                      </ButtonWithPromise>
                    )}
                    {ac.status === AutomationActionStatus.SKIPPED &&
                      !a.completedAt && (
                        <Button
                          startIcon={<Undo size={16} />}
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            onUnskipAction(ac.templateId);
                            e.preventDefault();
                          }}
                        >
                          {t('Unskip')}
                        </Button>
                      )}
                  </FlexContainer>
                )}
              </FlexContainer>
            </InnerItemRow>
          ))}
        </InnerItemGroup>
      </InnerItemList>
    </SelectableListItem>
  );
};
